import React, { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [selectedMonth, setSelectedMonth] = useState(7); // Juli is 7
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // States voor het nieuwe boekingsformulier
  const [newBooking, setNewBooking] = useState({
    apartment_id: "",
    start_date: "",
    end_date: "",
    price_per_day: "",
    number_of_guests: "",
  });

  // States voor het nieuwe uitgavenformulier
  const [newExpense, setNewExpense] = useState({
    apartment_id: "",
    expense_date: new Date().toISOString().split("T")[0],
    cleaning_supplies: "",
    towels: "",
    sheets: "",
    cleaning_hours: "",
    bedding: "",
    energy_costs: "",
    other_costs: "",
    description: "",
  });

  // Genereer een array van jaren (vanaf 2018 tot huidig jaar)
  const years = Array.from(
    { length: new Date().getFullYear() - 2018 + 1 },
    (_, i) => 2018 + i
  );

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/summary/monthly", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
      });

      if (!response.ok) {
        throw new Error("Kon geen gegevens ophalen");
      }

      const data = await response.json();
      setSummaryData(data.summary);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [selectedMonth, selectedYear]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("/api/bookings/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newBooking),
      });

      if (!response.ok) {
        throw new Error("Kon boeking niet toevoegen");
      }

      setNewBooking({
        apartment_id: "",
        start_date: "",
        end_date: "",
        price_per_day: "",
        number_of_guests: "",
      });

      fetchSummary();
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("/api/expenses/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newExpense,
          month: selectedMonth,
          year: selectedYear,
        }),
      });

      if (!response.ok) {
        throw new Error("Kon uitgaven niet toevoegen");
      }

      setNewExpense({
        apartment_id: "",
        cleaning_supplies: "",
        towels: "",
        sheets: "",
        cleaning_hours: "",
        bedding: "",
        energy_costs: "",
        other_costs: "",
        description: "",
      });

      fetchSummary();
    } catch (err) {
      setError(err.message);
      console.error(err);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Vakantieverhuur Administratie</h1>

      {/* Maand en jaar selectie */}
      <div className="mb-6">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="mr-4 p-2 border rounded"
        >
          <option value={7}>Juli</option>
          <option value={8}>Augustus</option>
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="p-2 border rounded"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Nieuwe boeking formulier */}
      <div className="mb-8 p-6 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-semibold mb-4">Nieuwe Boeking</h2>
        <form
          onSubmit={handleBookingSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <select
            value={newBooking.apartment_id}
            onChange={(e) =>
              setNewBooking({ ...newBooking, apartment_id: e.target.value })
            }
            className="p-2 border rounded"
            required
          >
            <option value="">Selecteer Appartement</option>
            <option value="1">Ático</option>
            <option value="2">2do Piso</option>
            <option value="3">Bajo</option>
          </select>

          <input
            type="date"
            value={newBooking.start_date}
            onChange={(e) =>
              setNewBooking({ ...newBooking, start_date: e.target.value })
            }
            className="p-2 border rounded"
            required
            placeholder="Start datum"
          />

          <input
            type="date"
            value={newBooking.end_date}
            onChange={(e) =>
              setNewBooking({ ...newBooking, end_date: e.target.value })
            }
            className="p-2 border rounded"
            required
            placeholder="Eind datum"
          />

          <input
            type="number"
            value={newBooking.price_per_day}
            onChange={(e) =>
              setNewBooking({ ...newBooking, price_per_day: e.target.value })
            }
            className="p-2 border rounded"
            required
            placeholder="Prijs per dag"
          />

          <input
            type="number"
            value={newBooking.number_of_guests}
            onChange={(e) =>
              setNewBooking({ ...newBooking, number_of_guests: e.target.value })
            }
            className="p-2 border rounded"
            required
            placeholder="Aantal gasten"
          />

          <button
            type="submit"
            className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
          >
            Boeking Toevoegen
          </button>
        </form>
      </div>

      {/* Nieuwe uitgaven formulier */}
      <div className="mb-8 p-6 bg-white rounded-lg shadow">
        <h2 className="text-2xl font-semibold mb-4">Nieuwe Uitgaven</h2>
        <form
          onSubmit={handleExpenseSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <select
            value={newExpense.apartment_id}
            onChange={(e) =>
              setNewExpense({ ...newExpense, apartment_id: e.target.value })
            }
            className="p-2 border rounded"
            required
          >
            <option value="">Selecteer Appartement</option>
            <option value="1">Ático</option>
            <option value="2">2do Piso</option>
            <option value="3">Bajo</option>
          </select>

          <input
            type="date"
            value={newExpense.expense_date}
            onChange={(e) =>
              setNewExpense({ ...newExpense, expense_date: e.target.value })
            }
            className="p-2 border rounded"
            required
          />

          <input
            type="number"
            value={newExpense.cleaning_supplies}
            onChange={(e) =>
              setNewExpense({
                ...newExpense,
                cleaning_supplies: e.target.value,
              })
            }
            className="p-2 border rounded"
            placeholder="Kuisproducten"
            step="0.01"
          />

          <input
            type="number"
            value={newExpense.towels}
            onChange={(e) =>
              setNewExpense({ ...newExpense, towels: e.target.value })
            }
            className="p-2 border rounded"
            placeholder="Handdoeken"
            step="0.01"
          />

          <input
            type="number"
            value={newExpense.sheets}
            onChange={(e) =>
              setNewExpense({ ...newExpense, sheets: e.target.value })
            }
            className="p-2 border rounded"
            placeholder="Lakens"
            step="0.01"
          />

          <input
            type="number"
            value={newExpense.cleaning_hours}
            onChange={(e) =>
              setNewExpense({ ...newExpense, cleaning_hours: e.target.value })
            }
            className="p-2 border rounded"
            placeholder="Werkuren kuisvrouw"
            step="0.01"
          />

          <input
            type="number"
            value={newExpense.bedding}
            onChange={(e) =>
              setNewExpense({ ...newExpense, bedding: e.target.value })
            }
            className="p-2 border rounded"
            placeholder="Aankoop bedgoed"
            step="0.01"
          />

          <input
            type="number"
            value={newExpense.energy_costs}
            onChange={(e) =>
              setNewExpense({ ...newExpense, energy_costs: e.target.value })
            }
            className="p-2 border rounded"
            placeholder="Energieverbruik"
            step="0.01"
          />

          <input
            type="number"
            value={newExpense.other_costs}
            onChange={(e) =>
              setNewExpense({ ...newExpense, other_costs: e.target.value })
            }
            className="p-2 border rounded"
            placeholder="Andere kosten"
            step="0.01"
          />

          <textarea
            value={newExpense.description}
            onChange={(e) =>
              setNewExpense({ ...newExpense, description: e.target.value })
            }
            className="p-2 border rounded col-span-2"
            placeholder="Beschrijving van de kosten (optioneel)"
            rows="2"
          />

          <button
            type="submit"
            className="bg-green-500 text-white p-2 rounded hover:bg-green-600 col-span-2"
          >
            Uitgaven Toevoegen
          </button>
        </form>
      </div>

      {/* Overzicht */}
      {error && <div className="text-red-500 mb-4">Error: {error}</div>}

      {loading ? (
        <div>Laden...</div>
      ) : (
        summaryData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {summaryData.map((apartment) => (
                <div
                  key={apartment.apartment_id}
                  className="bg-white p-6 rounded-lg shadow"
                >
                  <h3 className="text-xl font-semibold mb-4">
                    {apartment.apartment_name}
                  </h3>

                  {/* Maandelijkse cijfers */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-lg text-blue-600">
                      Deze Maand
                    </h4>
                    <p>
                      <strong>Totale Inkomsten:</strong> €
                      {apartment.total_income}
                    </p>
                    <p>
                      <strong>Aantal Boekingen:</strong>{" "}
                      {apartment.number_of_bookings}
                    </p>
                    <p>
                      <strong>Totaal Gasten:</strong> {apartment.total_guests}
                    </p>
                    <p>
                      <strong>Gemiddeld per Gast:</strong> €
                      {apartment.total_guests
                        ? (
                            apartment.total_income / apartment.total_guests
                          ).toFixed(2)
                        : 0}
                    </p>

                    <div className="mt-4">
                      <h4 className="font-semibold mb-2">
                        Uitgaven Deze Maand:
                      </h4>
                      <p>Kuisproducten: €{apartment.total_cleaning_supplies}</p>
                      <p>Handdoeken: €{apartment.total_towels}</p>
                      <p>Lakens: €{apartment.total_sheets}</p>
                      <p>Werkuren: €{apartment.total_cleaning_hours}</p>
                      <p>Bedgoed: €{apartment.total_bedding}</p>
                      <p>Energie: €{apartment.total_energy_costs}</p>
                      {apartment.total_other_costs > 0 && (
                        <>
                          <p>Andere kosten: €{apartment.total_other_costs}</p>
                          {apartment.other_costs_details && (
                            <p className="text-sm text-gray-600 mt-1">
                              Details: {apartment.other_costs_details}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <p className="text-lg font-bold">
                        Netto Winst Deze Maand: €{apartment.net_profit}
                      </p>
                    </div>
                  </div>

                  {/* Jaarlijkse cijfers */}
                  <div className="mt-8 pt-8 border-t space-y-2">
                    <h4 className="font-semibold text-lg text-green-600">
                      Dit Jaar
                    </h4>
                    <p>
                      <strong>Totale Inkomsten:</strong> €
                      {apartment.yearly_income}
                    </p>
                    <p>
                      <strong>Aantal Boekingen:</strong>{" "}
                      {apartment.yearly_bookings}
                    </p>
                    <p>
                      <strong>Totaal Gasten:</strong> {apartment.yearly_guests}
                    </p>
                    <p>
                      <strong>Gemiddeld per Gast:</strong> €
                      {apartment.yearly_guests
                        ? (
                            apartment.yearly_income / apartment.yearly_guests
                          ).toFixed(2)
                        : 0}
                    </p>

                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold mb-2">Uitgaven Dit Jaar:</h4>
                      <p>
                        Kuisproducten: €{apartment.yearly_cleaning_supplies}
                      </p>
                      <p>Handdoeken: €{apartment.yearly_towels}</p>
                      <p>Lakens: €{apartment.yearly_sheets}</p>
                      <p>Werkuren: €{apartment.yearly_cleaning_hours}</p>
                      <p>Bedgoed: €{apartment.yearly_bedding}</p>
                      <p>Energie: €{apartment.yearly_energy_costs}</p>
                      {apartment.yearly_other_costs > 0 && (
                        <p>Andere kosten: €{apartment.yearly_other_costs}</p>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <p className="text-lg font-bold">
                        Netto Winst Dit Jaar: €{apartment.yearly_net_profit}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totalen voor alle appartementen */}
            <div className="mt-8 bg-gray-100 p-6 rounded-lg shadow">
              <h3 className="text-2xl font-semibold mb-6">
                Totalen Alle Appartementen
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Maandtotalen */}
                <div>
                  <h4 className="text-lg font-semibold text-blue-600 mb-4">
                    Deze Maand
                  </h4>
                  <div className="space-y-2">
                    <p>
                      <strong>Totale Inkomsten:</strong> €
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.total_income,
                        0
                      )}
                    </p>
                    <p>
                      <strong>Totaal Boekingen:</strong>{" "}
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.number_of_bookings,
                        0
                      )}
                    </p>
                    <p>
                      <strong>Totaal Gasten:</strong>{" "}
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.total_guests,
                        0
                      )}
                    </p>
                    <p>
                      <strong>Totale Netto Winst:</strong> €
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.net_profit,
                        0
                      )}
                    </p>
                  </div>
                </div>

                {/* Jaartotalen */}
                <div>
                  <h4 className="text-lg font-semibold text-green-600 mb-4">
                    Dit Jaar
                  </h4>
                  <div className="space-y-2">
                    <p>
                      <strong>Totale Inkomsten:</strong> €
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.yearly_income,
                        0
                      )}
                    </p>
                    <p>
                      <strong>Totaal Boekingen:</strong>{" "}
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.yearly_bookings,
                        0
                      )}
                    </p>
                    <p>
                      <strong>Totaal Gasten:</strong>{" "}
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.yearly_guests,
                        0
                      )}
                    </p>
                    <p>
                      <strong>Totale Netto Winst:</strong> €
                      {summaryData.reduce(
                        (sum, apt) => sum + apt.yearly_net_profit,
                        0
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

export default App; 